import { useCallback, useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Header } from '@trinity/chat-components/components/Modal/Header';
import { Modal } from '@trinity/chat-components/components/Modal/Modal';
import Button from '@trinity/chat-components/components/presentation/Button/Button';
import styles from './styles/TableauDashboardModal.module.css';

type Props = {
  open: boolean;
  onClose: () => void;
  initialDashboardId?: string;
  initialTabName?: string;
  /** Defaults to the create wording; an edit flow passes its own. */
  title?: string;
  updateTableauDashboard: (data: {
    dashboardId: string;
    tabName: string;
  }) => Promise<void>;
};

export const TableauDashboardModal = ({
  open,
  onClose,
  initialDashboardId = '',
  initialTabName = '',
  title = 'Enter Tableau Connection Details',
  updateTableauDashboard,
}: Props) => {
  const [tabName, setTabName] = useState<string>(initialTabName);
  const [dashboardId, setDashboardId] = useState<string>(initialDashboardId);

  const { isPending: isSubmitting, mutateAsync } = useMutation({
    mutationFn: () => {
      const finalTabName = (tabName || '').trim() || 'Tableau Dashboard';
      return updateTableauDashboard({
        dashboardId,
        tabName: finalTabName,
      });
    },
    onSuccess: () => {
      onClose();
    },
  });

  const handleSubmit = useCallback(() => {
    return mutateAsync();
  }, [mutateAsync]);

  const submitDisabled = useMemo(() => {
    return isSubmitting || !(dashboardId || '').trim();
  }, [isSubmitting, dashboardId]);

  return (
    <Modal open={open} onClose={onClose} className={styles.modal}>
      <Header text={title} />
      <div className={styles.body}>
        <div className={styles.inputsContainer}>
          <div className={styles.label}>Dashboard Tab Name</div>
          <input
            className={styles.input}
            placeholder="Dashboard Name"
            value={tabName}
            onChange={e => setTabName(e.target.value)}
          />
          <div className={styles.label} style={{ marginTop: 24 }}>
            Tableau Dashboard URL
          </div>
          <input
            className={`${styles.input} ${styles.dashboardUrlInput}`}
            style={{ marginBottom: 12 }}
            placeholder="Dashboard URL"
            value={dashboardId}
            onChange={e => setDashboardId(e.target.value)}
          />

          <div className={styles.infoMessage}>
            * The Dashboard URL can be obtained from the &quot;Share&quot; or &quot;Embed&quot;
            option of your Tableau dashboard. It typically looks like:{' '}
          </div>
          <div className={styles.exampleUrl}>
            https://[your-tableau-server]/views/[dashboard-name]
          </div>
        </div>
      </div>
      <div className={styles.footer}>
        <Button
          className={styles.cancel}
          variant="brick"
          color="secondary"
          onClick={onClose}>
          Cancel
        </Button>
        <Button
          className={styles.continue}
          variant="brick"
          disabled={submitDisabled}
          onClick={handleSubmit}>
          Continue
        </Button>
      </div>
    </Modal>
  );
};
